
import React from 'react';
import QRCode from 'qrcode';
import { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '@/components/ui/button';
import { Download, FileImage, FileText } from 'lucide-react';

interface WindowStickerTemplateProps {
  providerSlug?: string;
  qrCodeUrl?: string;
}

const WindowStickerTemplate = ({ 
  providerSlug = "your-provider", 
  qrCodeUrl = "https://wellsuites.com" 
}: WindowStickerTemplateProps) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const stickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(qrCodeUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeDataUrl(dataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [qrCodeUrl]);

  const handlePrint = () => {
    window.print();
  };

  const exportAsImage = async () => {
    if (!stickerRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(stickerRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `window-sticker-classic-${Date.now()}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error exporting as image:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!stickerRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(stickerRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a5' // A5 size as recommended
      });
      
      const imgWidth = 148; // A5 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`window-sticker-classic-${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error exporting as PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      {/* Print Controls - Hidden when printing */}
      <div className="mb-8 print:hidden">
        <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold mb-4 text-center">Window Sticker Template</h2>
          <p className="text-gray-600 mb-4 text-center">
            Preview your window sticker before printing or exporting.
          </p>

          {/* Export Buttons */}
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={exportAsImage}
                disabled={isExporting}
                variant="outline"
                className="w-full"
              >
                <FileImage className="w-4 h-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export PNG'}
              </Button>
              <Button
                onClick={exportAsPDF}
                disabled={isExporting}
                variant="outline"
                className="w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </Button>
            </div>
          </div>

          <button
            onClick={handlePrint}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
          >
            Print Sticker
          </button>
        </div>
      </div>

      {/* Window Sticker Template */}
      <div className="max-w-md mx-auto">
        <div 
          ref={stickerRef}
          className="bg-white border-4 border-dashed border-gray-300 print:border-none print:shadow-none shadow-lg rounded-lg overflow-hidden"
        >
          {/* Main Content Area */}
          <div className="p-8 text-center">
            {/* Main Heading */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 leading-tight">
                Book a session<br />online
              </h1>
            </div>

            {/* QR Code Section */}
            <div className="mb-8 flex justify-center">
              <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200">
                {qrCodeDataUrl ? (
                  <img 
                    src={qrCodeDataUrl} 
                    alt="QR Code to book online" 
                    className="w-48 h-48 mx-auto"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500">Loading QR Code...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-8">
              <p className="text-lg text-gray-700">
                Scan to book your appointment
              </p>
            </div>
          </div>

          {/* Footer - Powered by Wellsuites */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8">
            <div className="flex items-center justify-center space-x-3">
              <span className="text-xs font-medium">powered by</span>
              <div className="flex items-center">
                {/* Wellsuites White Logo */}
                <img 
                  src="/lovable-uploads/5ed6cde8-cc84-4a75-9096-19940a8aca66.png" 
                  alt="Wellsuites Logo" 
                  className="h-3 w-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Print Guidelines - Hidden when printing */}
        <div className="mt-6 bg-white p-4 rounded-lg shadow print:hidden">
          <h3 className="font-semibold mb-2">Export & Print Guidelines:</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Export as PNG for digital use or sharing</li>
            <li>• Export as PDF for professional printing</li>
            <li>• Use high-quality sticker paper or cardstock</li>
            <li>• Print at 100% scale (no scaling)</li>
            <li>• Recommended size: A5 (148 × 210 mm)</li>
            <li>• Use color printer for best results</li>
          </ul>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:border-none {
            border: none !important;
          }
          
          .print\\:shadow-none {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WindowStickerTemplate;
